// src/pages/FundPlanner/components/Portfolios/useFileManager.js
import { useMemo, useCallback } from "react";
import useFileManager from "components/FileManager/hooks/useFileManager";
import { useProfile } from "context/ProfileContext";
import { getSharingPermissions } from "lib/permissions";

export function usePortfoliosFileManager({ limit = 100 } = {}) {
  const { profile, selectedOrg } = useProfile();

  const {
    records: rawRecords,
    recents,
    loading,
    error,
    fetchRecords,
    addToRecents,
  } = useFileManager({
    table: "fp_portfolios",
    org: selectedOrg,
    limit,
    select: `
      id,
      org_id,
      tenant_id,
      created_by,
      name,
      sharing,
      created_at,
      updated_at
    `,
  });

  const records = useMemo(() => {
    if (!Array.isArray(rawRecords)) return [];

    return rawRecords.map((r) => ({
      ...r,
      ...getSharingPermissions(r, profile),
    }));
  }, [rawRecords, profile]);

  const openRecord = useCallback(
    (portfolio) => {
      addToRecents?.(portfolio);
    },
    [addToRecents]
  );

  const isReadOnlyRecord = useCallback(
    (r) => getSharingPermissions(r, profile).isReadOnly,
    [profile]
  );

  const hasAccessError = useMemo(() => {
    if (!selectedOrg) return false;
    if (loading || error) return false;
    return Array.isArray(records) && records.length === 0;
  }, [selectedOrg, loading, error, records]);

  return {
    selectedOrg,
    records,
    recents,
    loading,
    error,
    refresh: fetchRecords,
    openRecord,
    isReadOnlyRecord,
    hasAccessError,
    newLabel: "New Portfolio",
  };
}

function normStr(x) {
  return String(x ?? "").trim();
}

function getPortfolioName(r) {
  return normStr(r?.name) || "(Untitled Portfolio)";
}

function formatDateShort(value) {
  const s = String(value ?? "").trim();
  return s ? s.slice(0, 10) : "";
}

const COL_NAME = "w-[70%]";
const COL_SMALL = "w-[30%]";

export const portfoliosOpenView = {
  title: "Open Portfolio",

  defaultSort: { key: "name", asc: true },

  search: {
    placeholder: "Search portfolios…",
    predicate: (r, q) => {
      const name = getPortfolioName(r).toLowerCase();
      return name.includes(q);
    },
  },

  selectedLabel: (r) => getPortfolioName(r),

  columns: [
    {
      key: "name",
      header: "Portfolio",
      align: "left",
      headerClassName: COL_NAME,
      cellClassName: COL_NAME,
      sortValue: (r) => getPortfolioName(r).toLowerCase(),
      showReadOnlyBadge: true,
      render: (r) => {
        const name = getPortfolioName(r);
        return (
          <div className="truncate" title={name}>
            {name}
          </div>
        );
      },
    },
    {
      key: "updated_at",
      header: "Updated",
      align: "left",
      headerClassName: COL_SMALL,
      cellClassName: `${COL_SMALL} whitespace-nowrap tabular-nums`,
      defaultAsc: false,
      sortValue: (r) => normStr(r?.updated_at),
      render: (r) => {
        const shown = formatDateShort(r?.updated_at);
        return shown ? (
          <div className="truncate" title={shown}>
            {shown}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
  ],
};
